import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Stack, Step as UIStep, StepLabel, Typography, Stepper as UIStepper, useTheme } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'

import { toast } from 'components/global/CUK/Toast/Toast'
import TreeProvider from 'packages/ui/Arborist/TreeProvider'
import buildSchema from './schema'
import { useAdvertisement } from '../use-advertisements'
import EditModal from './EditModal'
import Content from './Steps/Content'
import Image from './Steps/Image'
import AccountsPerimeter from './Steps/AccountsPerimeter'

import useSave, { DEFAULT_ADVERTISEMENT } from './use-save'

const STEPS = [
  {
    label: 'advertisements.edition.modal.content',
    icon: <i className="fa-regular fa-text"></i>
  },
  {
    label: 'advertisements.edition.modal.picture',
    icon: <i className="fa-regular fa-image"></i>
  },
  {
    label: 'advertisements.edition.modal.sites',
    icon: <i className="fa-regular fa-location-dot"></i>
  }
]

function StepperIcon(props) {
  const { icon, active, hasError, id } = props
  const theme = useTheme()
  const color = active ? 'primary.main' : hasError ? 'error.main' : 'content.secondary'
  const iconColor = active ? theme.palette.primary.main : hasError ? theme.palette.error.main : 'inherit'

  return (
    <Box
      sx={{
        borderRadius: '50%',
        cursor: id && 'pointer',
        display: 'flex',
        width: '32px',
        height: '32px',
        border: 'solid 1px',
        borderColor: active ? 'primary.main' : 'border.primary',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        '& i, & svg': {
          color: 'inherit'
        },
        '&:hover': {
          color: id && 'primary.main'
        }
      }}>
      {React.cloneElement(icon, {
        style: {
          ...icon.props.style,
          color: iconColor
        }
      })}
    </Box>
  )
}

const Step = ({ onClick, label, icon, hasError, active }) => {
  const { id } = useParams()
  const { t } = useTranslation()
  const color = active ? 'primary.main' : hasError ? 'error.main' : 'inherit'

  return (
    <UIStep
      sx={{
        // Base label style
        '& .MuiStepLabel-label': {
          mt: '4px',
          mb: '4px'
        },

        // Style for completed step label
        '& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel': {
          color: 'content.secondary',
          fontWeight: '400'
        },
        '& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel': {
          color: 'primary.main'
        },
        '&:hover .MuiStepLabel-label, &:hover .MuiStepIcon-root': {
          color: id && 'primary.main'
        }
      }}>
      <StepLabel
        onClick={() => {
          id ? onClick() : null
        }}
        StepIconComponent={props => <StepperIcon {...props} active={active} icon={icon} hasError={hasError} id={id} />}
        sx={{
          '& .MuiStepLabel-label': {
            mt: '5px !important',
            cursor: id && 'pointer',
            color
          },
          '& .Mui-active': {
            color: 'primary.main'
          }
        }}>
        {t(label)}
      </StepLabel>
    </UIStep>
  )
}

const Stepper = ({ activeStep, hasContentError, hasImageError, hasAccountsError, onClick = () => {} }) => {
  return (
    <UIStepper
      alternativeLabel
      activeStep={activeStep}
      sx={{
        width: '350px',
        '& .MuiStep-root:first-child .MuiStepConnector-root': {
          visibility: 'hidden'
        }
      }}>
      {STEPS.map((step, i) => (
        <Step
          key={i}
          active={activeStep === i}
          hasError={i === 0 ? hasContentError : i === 1 ? hasImageError : i === 2 ? hasAccountsError : false}
          onClick={() => onClick(i)}
          {...step}
        />
      ))}
    </UIStepper>
  )
}
const isContentError = errors => {
  return Boolean(errors.name || errors.starts_at || errors.ends_at || errors.contentCompleted)
}
const isImageError = errors => Boolean(errors.image)
const isAccountsError = errors => Boolean(errors.accounts)

const Edit = ({ advertisement }) => {
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)
  const navigate = useNavigate()
  const { id } = useParams()
  const [selectedStep, setSelectedStep] = useState(0)
  const { t } = useTranslation()
  const [save, { loading, error }] = useSave()
  const methods = useForm({
    defaultValues: {
      ...advertisement,
      accounts: advertisement.accounts.filter(({ is_site }) => is_site).map(({ sf_id }) => sf_id)
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(buildSchema(t))
  })
  const isDirty = methods.formState.isDirty
  const hasContentError = isContentError(methods.formState.errors)
  const hasImageError = isImageError(methods.formState.errors)
  const hasAccountsError = isAccountsError(methods.formState.errors)

  const handleSubmit = async (step, values) => {
    if (step < 2) {
      setSelectedStep(selectedStep + 1)
    } else if (step === 2) {
      const result = await save(values)
      if (result) {
        navigate(`/advertisements/${result.id}`)
      }
    }
  }

  const handleError = (step, errors) => {
    if (id) {
      return
    } else if (step === 0 && !isContentError(errors)) {
      setSelectedStep(1)
    } else if (step === 1 && !isImageError(errors)) {
      setSelectedStep(2)
    }
  }
  return (
    <FormProvider {...methods}>
      <EditModal
        showHeader={!confirmCloseOpen}
        onClose={() => {
          if (isDirty) {
            setConfirmCloseOpen(true)
          } else {
            navigate('..')
          }
        }}
        onCloseConfirmation={confirmCloseOpen ? () => setConfirmCloseOpen(false) : null}>
        <Stack alignItems="center" gap={3} sx={{ height: '100%', minHeight: 0 }}>
          <Stepper
            onClick={setSelectedStep}
            activeStep={selectedStep}
            hasContentError={hasContentError}
            hasImageError={hasImageError}
            hasAccountsError={hasAccountsError}
          />
          <Box sx={{ width: '100%', height: '100%', flex: 1, minHeight: 0, maxWidth: '600px' }}>
            {Boolean(error) && (
              <Typography variant={'caption'} color={'error'}>
                {error}
              </Typography>
            )}
            {selectedStep === 0 && (
              <Content
                onSubmit={methods.handleSubmit(
                  values => handleSubmit(0, values),
                  errors => handleError(0, errors)
                )}
                loading={loading}
              />
            )}
            {selectedStep === 1 && (
              <Image
                image={advertisement.image}
                onSubmit={methods.handleSubmit(
                  values => handleSubmit(1, values),
                  errors => handleError(1, errors)
                )}
                onGoBack={() => setSelectedStep(0)}
                loading={loading}
              />
            )}
            {selectedStep === 2 && (
              <AccountsPerimeter
                onSubmit={methods.handleSubmit(values => handleSubmit(2, values))}
                onGoBack={() => setSelectedStep(1)}
                loading={loading}
              />
            )}
          </Box>
        </Stack>
      </EditModal>
    </FormProvider>
  )
}

export const EditLoader = () => {
  const { id } = useParams()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { advertisement, loading, error } = useAdvertisement(
    { id },
    {
      skip: !id,
      onError: () => {
        toast.error(t('advertisements.edition.toaster.get_adv_error'))
      }
    }
  )

  return (
    <EditModal onClose={() => navigate('..')}>
      {loading ? (
        <Stack alignItems={'center'} gap={2}>
          <Stepper />
          <Typography variant={'caption'}>{t('ui_elements.loading_msg')}</Typography>
        </Stack>
      ) : error ? (
        <Typography variant={'caption'} color={'error'}>
          {error?.messge}
        </Typography>
      ) : (
        <TreeProvider initialSelection={id ? advertisement.accounts : []}>
          <Edit advertisement={id ? advertisement : { ...DEFAULT_ADVERTISEMENT, fallback_language: i18n.language }} />
        </TreeProvider>
      )}
    </EditModal>
  )
}

export default EditLoader
